// @ts-check
/**
 * Sdílené načtení a výměna HTML fragmentu (R-15 sekce 2, ui-checklist bod 13) — jedna knihovna
 * pro všechny moduly, bez znalosti konkrétní stránky (žádný selektor výpisu ani detailu).
 *
 * `fetchFragment(url, { signal, fetch })`: `GET url` s hlavičkou `Accept: text/html; fragment`
 * (server vrátí tutéž šablonu bez layoutu, `webui.Render`). Stav ≠ 200 nebo `Content-Type` jiný
 * než `text/html` = výjimka `FragmentError`; tělo projde `DOMParser` (nikdy `innerHTML`). Vrací
 * `{ doc, url }`: `url` je cesta a dotaz z hlavičky `Link: <…>; rel="canonical"` odpovědi
 * (kanonickou podobu zná jen server; `history.replaceState` s jiným původem by vyhodil
 * `SecurityError`, proto jen `pathname + search`), bez hlavičky adresa požadavku. Zrušení přes
 * `signal` propadne jako `AbortError` z `fetch` — není to selhání, rozhoduje volající.
 *
 * `swap(root, doc, selector)`: nahradí DĚTI prvku `selector` v `root` dětmi téhož selektoru
 * z `doc` (`ownerDocument.importNode` + `replaceChildren`), prvek sám zůstává — živá oblast
 * (`aria-live`) tak existuje před změnou a čtečka ji ohlásí. Chybějící prvek na kterékoli
 * straně = výjimka, `root` beze změny.
 *
 * `append(root, doc, selector)`: PŘIPOJÍ za děti prvku `selector` v `root` kopie elementových
 * dětí (`children`, ne textové uzly) téhož prvku z `doc` v původním pořadí a vrátí pole
 * připojených prvků — kopie v kořeni, ne uzly fragmentu (volající na nich smí fokusovat).
 * Chybějící prvek = výjimka jako u `swap` (R-15 změna 18. 10. 2026, připojení další strany).
 *
 * Žádné texty UI, žádný globální stav; dokument se bere z `root.ownerDocument`, `fetch`
 * a `DOMParser` z parametrů nebo z `globalThis` (jsdom testy je vstřikují).
 */

const ACCEPT = 'text/html; fragment';
const CANONICAL = /<([^>]*)>\s*;[^,]*\brel="?canonical"?/;

export class FragmentError extends Error {
  /**
   * @param {string} message
   * @param {string} url
   */
  constructor(message, url) {
    super(`${message} (${url})`);
    this.name = 'FragmentError';
  }
}

/**
 * Cesta a dotaz z hlavičky `Link` s `rel="canonical"`, jinak `fallback`.
 * @param {string | null} link hodnota hlavičky `Link`
 * @param {string} fallback adresa požadavku
 * @param {string} base původ dokumentu pro relativní adresy
 * @returns {string}
 */
export function canonicalPath(link, fallback, base) {
  if (link === null) return fallback;
  for (const part of link.split(',')) {
    const m = CANONICAL.exec(part);
    if (m === null) continue;
    try {
      const u = new URL(m[1], base);
      return u.pathname + u.search;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

/**
 * Minimum z `Response`, které knihovna čte (testy vstřikují prostý objekt).
 * @typedef {{ status: number, headers: { get(name: string): string | null }, text(): Promise<string> }} FragmentResponse
 * @typedef {(url: string, init: { headers: Record<string, string>, signal?: AbortSignal }) => Promise<FragmentResponse>} Fetcher
 */

/**
 * @typedef {object} FetchOptions
 * @property {AbortSignal} [signal] zrušení předchozího požadavku
 * @property {Fetcher} [fetch] náhrada `fetch` (testy)
 * @property {typeof globalThis.DOMParser} [parser] náhrada `DOMParser` (testy)
 * @property {string} [base] původ pro relativní adresu v `Link` (výchozí `location.href`)
 */

/**
 * Načte fragment stránky `url` a rozparsuje ho. `title` je text `<title>` ze začátku fragmentu
 * (server ho dává každému fragmentu stránky, R-23; `DOMParser` ho uloží do `head`, `Document.title`
 * vrátí text s ořezanými mezerami) — bez `<title>` prázdný řetězec, modul pak titulek nemění.
 * @param {string} url cesta a dotaz téže stránky
 * @param {FetchOptions} [options]
 * @returns {Promise<{ doc: Document, url: string, title: string }>}
 */
export async function fetchFragment(url, options = {}) {
  const doFetch = options.fetch ?? /** @type {Fetcher} */ (globalThis.fetch);
  const Parser = options.parser ?? globalThis.DOMParser;
  const res = await doFetch(url, { headers: { Accept: ACCEPT }, signal: options.signal });
  if (res.status !== 200) throw new FragmentError(`stav ${res.status}`, url);
  const type = res.headers.get('Content-Type') ?? '';
  if (!/^text\/html\b/i.test(type)) throw new FragmentError(`Content-Type ${type || '—'}`, url);
  const text = await res.text();
  const doc = new Parser().parseFromString(text, 'text/html');
  const base = options.base ?? globalThis.location?.href ?? 'http://localhost/';
  return { doc, url: canonicalPath(res.headers.get('Link'), url, base), title: doc.title };
}

/**
 * Vymění děti prvku `selector` v `root` za děti téhož prvku z `doc`.
 * @param {HTMLElement} root kořen modulu
 * @param {Document} doc rozparsovaný fragment
 * @param {string} selector společný selektor obou stran
 * @returns {void}
 */
export function swap(root, doc, selector) {
  const { target, source } = ends(root, doc, selector);
  const imported = root.ownerDocument.importNode(source, true);
  target.replaceChildren(...Array.from(imported.childNodes));
}

/**
 * Oba konce výměny nebo připojení; chybějící strana = výjimka.
 * @param {HTMLElement} root
 * @param {Document} doc
 * @param {string} selector
 * @returns {{ target: Element, source: Element }}
 */
function ends(root, doc, selector) {
  const target = root.querySelector(selector);
  const source = doc.querySelector(selector);
  if (target === null) throw new FragmentError(`v kořeni chybí ${selector}`, '');
  if (source === null) throw new FragmentError(`ve fragmentu chybí ${selector}`, '');
  return { target, source };
}

/**
 * Připojí za děti prvku `selector` v `root` kopie elementových dětí téhož prvku z `doc`.
 * @param {HTMLElement} root kořen modulu
 * @param {Document} doc rozparsovaný fragment
 * @param {string} selector společný selektor obou stran
 * @returns {Element[]} připojené prvky (kopie v kořeni) v pořadí fragmentu
 */
export function append(root, doc, selector) {
  const { target, source } = ends(root, doc, selector);
  const added = Array.from(
    source.children,
    (child) => /** @type {Element} */ (root.ownerDocument.importNode(child, true)),
  );
  target.append(...added);
  return added;
}
